import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectUser } from '../redux/auth/authSlice';
import { useAppSelector } from '../redux/hooks';
import useLogin from './auth/useLogin';

const useUserRedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectUser);
  const { loginUser } = useLogin();
  const [isProcessingToken, setIsProcessingToken] = useState(true);

  useEffect(() => {
    const handleAccessToken = async () => {
      const urlParams = new URLSearchParams(location.search);
      const accessToken = urlParams.get('accessToken');

      if (!accessToken) {
        // No accessToken in URL, we can proceed immediately
        setIsProcessingToken(false);
        return;
      }

      try {
        // Check if there's already a user logged in
        if (currentUser) {
          // User is already logged in, just remove the accessToken from URL
          urlParams.delete('accessToken');
          const newSearch = urlParams.toString();
          const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
          navigate(newUrl, { replace: true });
          setIsProcessingToken(false);
          return;
        }

        // No user logged in, proceed with login using the token
        await loginUser({ accessToken });

        // Remove the accessToken from URL
        urlParams.delete('accessToken');
        const newSearch = urlParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        navigate(newUrl, { replace: true });
      } catch (error) {
        console.error('Failed to login with access token:', error);
        // Remove invalid token from URL
        urlParams.delete('accessToken');
        const newSearch = urlParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        navigate(newUrl, { replace: true });
      } finally {
        setIsProcessingToken(false);
      }
    };

    handleAccessToken();
  }, [location.search, currentUser, navigate, location.pathname, loginUser]);

  return { isProcessingToken };
};

export default useUserRedirectHandler;
